import { DropdownItemProps } from "semantic-ui-react";
import { toast } from "..";
import { IParameter, IParameterFilter } from "../models/parameter";
import { getAuthorizationProperties } from "./authorizationHelper";

export async function getParameter(id: string): Promise<IParameter> {
    const data = await fetch(`/api/parameters/${id}`, {
        method: "GET",
        ...getAuthorizationProperties({
            "Content-Type": "application/json",
        }),
    }).then(res => {
        return res.json();
    });
    return data;
}

export async function getParameters(
    filter: IParameterFilter,
    page: number,
    items: number,
    isDevelopment: boolean,
    withSummary: boolean
): Promise<{ result: IParameter[]; totalRows: number }> {
    const params = new URLSearchParams({
        page: page.toString(),
        items: items.toString(),
        isDevelopment: isDevelopment.toString(),
        withSummary: withSummary.toString(),
    });
    Object.entries(filter).forEach(x => {
        if (x[1] !== undefined && x[1] !== null && x[1] !== "") {
            params.append(x[0], x[1]);
        }
    });
    return await fetch(`/api/parameters?${params.toString()}`, {
        method: "GET",
        ...getAuthorizationProperties({ "Content-Type": "application/json" }),
    }).then(res => {
        if (res.ok) {
            return res.json();
        }
        throw Error;
    });
}

export async function createParameter(parameter: IParameter): Promise<IParameter> {
    return await fetch("/api/parameters/", {
        method: "POST",
        ...getAuthorizationProperties({ "Content-Type": "application/json" }),
        body: JSON.stringify(parameter),
    }).then(res => {
        if (res.ok) {
            return res.json();
        } else if (res.status === 409) {
            toast("Parameter name must be unique", false, 1000);
            return null;
        }

        throw Error;
    });
}

export async function editParameter(
    id: string,
    parameter: Partial<IParameter>
): Promise<IParameter> {
    return await fetch(`/api/parameters/${id}`, {
        method: "PUT",
        ...getAuthorizationProperties({ "Content-Type": "application/json" }),
        body: JSON.stringify(parameter),
    }).then(res => {
        if (res.ok) {
            return res.json();
        } else if (res.status === 409) {
            toast("Parameter name must be unique", false, 1000);
            return null;
        }
        throw Error;
    });
}

export async function getParamtersToSubmit(): Promise<IParameter[]> {
    return await fetch("/api/parameters/toSubmit", {
        method: "GET",
        ...getAuthorizationProperties({ "Content-Type": "application/json" }),
    }).then(res => {
        return res.json();
    });
}

export async function getPublishedParameters(): Promise<IParameter[]> {
    let res = await fetch("/api/parameters/published", {
        method: "GET",
        ...getAuthorizationProperties({ "Content-Type": "application/json" }),
    });
    return res.json();
}

export async function getInProgressParameters(): Promise<IParameter[]> {
    let res = await fetch("/api/parameters/inProgress", {
        method: "GET",
        ...getAuthorizationProperties({ "Content-Type": "application/json" }),
    });
    return res.json();
}

export async function publishParameter(parameterGuid: string) {
    let res = await fetch(`/api/parameters/publish/${parameterGuid}`, {
        method: "PATCH",
        ...getAuthorizationProperties({ "Content-Type": "application/json" }),
    });
    return res.ok;
}

export async function unpublishParameter(parameterGuid: string) {
    let res = await fetch(`/api/parameters/unpublish/${parameterGuid}`, {
        method: "PATCH",
        ...getAuthorizationProperties({
            "Content-Type": "application/json",
        }),
    });

    if (!res.ok) {
        const message = await res.text();
        if (message) {
            throw new Error(message);
        } else {
            throw new Error("Failed to unpublish parameter");
        }
    }
}

export async function updateParameterVisibility(parameterGuid: string) {
    await fetch(`/api/parameters/visibility/${parameterGuid}`, {
        method: "PATCH",
        ...getAuthorizationProperties({
            "Content-Type": "application/json",
        }),
    });
}

export async function getActiveParameters(): Promise<IParameter[]> {
    let res = await fetch("/api/parameters/active", {
        method: "GET",
        ...getAuthorizationProperties({ "Content-Type": "application/json" }),
    });
    return res.json();
}

export async function getParameterCategoriesAsProps(): Promise<DropdownItemProps[]> {
    let res = await fetch("/api/parameters/categories/props", {
        method: "GET",
        ...getAuthorizationProperties({ "Content-Type": "application/json" }),
    });
    return res.json();
}

export async function deleteParameter(guid: string) {
    const res = await fetch(`/api/parameters/${guid}`, {
        method: "DELETE",
        ...getAuthorizationProperties({ "Content-Type": "application/json" }),
    });
    return res.ok;
}

export async function hideParameter(guid: string) {
    const res = await fetch(`/api/parameters/hide/${guid}`, {
        method: "PATCH",
        ...getAuthorizationProperties({ "Content-Type": "application/json" }),
    });
    return res.ok;
}
